import React, { useState, useEffect } from "react";
import {
  Box, Card, Container, Typography, Grid, ToggleButton, ToggleButtonGroup,
  Stack, Divider, CircularProgress,
} from "@mui/material";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { collection, getDocs, query, orderBy, where, Timestamp } from "firebase/firestore";
import { db } from "../database/db";
import Page from "../components/Page";

const GREEN = "#3dc10e";
const ORANGE = "#f28c3e";
const BLUE = "#4FC3F7";
const PURPLE = "#CE93D8";

function StatCard({ title, value, sub, color = GREEN }) {
  return (
    <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)", height: "100%" }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
        {title}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 800, color, mt: 0.5 }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Card>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box sx={{ bgcolor: "#222", border: "1px solid #333", borderRadius: 2, p: 1.5 }}>
        <Typography variant="caption" sx={{ color: "#aaa", display: "block", mb: 0.5 }}>{label}</Typography>
        {payload.map((p) => (
          <Typography key={p.name} variant="caption" sx={{ color: p.color, display: "block" }}>
            {p.name}: {typeof p.value === "number" && p.name.toLowerCase().includes("revenue") ? `$${p.value.toFixed(2)}` : p.value}
          </Typography>
        ))}
      </Box>
    );
  }
  return null;
};

function buildDailyBuckets(items, dateField, valueField, days) {
  const buckets = {};
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    buckets[key] = 0;
  }
  items.forEach((item) => {
    const ts = item[dateField];
    if (!ts) return;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const key = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    if (key in buckets) {
      buckets[key] += valueField ? (item[valueField] || 0) : 1;
    }
  });
  return Object.entries(buckets).map(([date, value]) => ({ date, value }));
}

function buildWeeklyBuckets(items, dateField, valueField, weeks) {
  const buckets = {};
  const now = new Date();
  for (let i = weeks - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i * 7);
    const key = `Wk ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
    buckets[key] = 0;
  }
  items.forEach((item) => {
    const ts = item[dateField];
    if (!ts) return;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const weekAgo = new Date(now);
    weekAgo.setDate(weekAgo.getDate() - weeks * 7);
    if (d < weekAgo) return;
    const dayDiff = Math.floor((now - d) / (1000 * 60 * 60 * 24));
    const weekIdx = weeks - 1 - Math.floor(dayDiff / 7);
    if (weekIdx < 0 || weekIdx >= weeks) return;
    const wDate = new Date(now);
    wDate.setDate(wDate.getDate() - (weeks - 1 - weekIdx) * 7);
    const key = `Wk ${wDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
    if (key in buckets) buckets[key] += valueField ? (item[valueField] || 0) : 1;
  });
  return Object.entries(buckets).map(([date, value]) => ({ date, value }));
}

export default function RevenueCharts() {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState({ tokens: [], subs: [], users: [], churn: [] });
  const [totals, setTotals] = useState({ tokenRevenue: 0, subRevenue: 0, newUsers: 0, churnRate: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [range]);

  const loadData = async () => {
    setLoading(true);
    try {
      const days = range === "7d" ? 7 : range === "30d" ? 30 : 90;
      const weeks = Math.ceil(days / 7);
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);
      const cutoffTs = Timestamp.fromDate(cutoff);

      const [tokenSnap, subSnap, userSnap] = await Promise.all([
        getDocs(query(collection(db, "TokenTransactions"), orderBy("createdAt", "desc"))),
        getDocs(query(collection(db, "Subscriptions"), orderBy("createdAt", "desc"))),
        getDocs(query(collection(db, "User"), orderBy("createdAt", "desc"))),
      ]);

      const tokens = tokenSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const subs = subSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const users = userSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const totalTokenRev = tokens.reduce((s, t) => s + (t.amount || 0), 0);
      const totalSubRev = subs.reduce((s, t) => s + (t.amount || 0), 0);
      const newUsersCount = users.filter((u) => {
        if (!u.createdAt) return false;
        const d = u.createdAt.toDate ? u.createdAt.toDate() : new Date(u.createdAt);
        return d > cutoff;
      }).length;

      const builder = days <= 30 ? buildDailyBuckets : buildWeeklyBuckets;
      const countArg = days <= 30 ? days : weeks;

      const tokenData = builder(tokens, "createdAt", "amount", countArg).map((b) => ({ ...b, "Token Revenue": b.value }));
      const subData = builder(subs, "createdAt", "amount", countArg).map((b) => ({ ...b, "Sub Revenue": b.value }));
      const userData = builder(users, "createdAt", null, countArg).map((b) => ({ ...b, "New Users": b.value }));

      // Merge into single combined dataset
      const combined = tokenData.map((t, i) => ({
        date: t.date,
        "Token Revenue": t["Token Revenue"],
        "Sub Revenue": subData[i]?.["Sub Revenue"] || 0,
        "New Users": userData[i]?.["New Users"] || 0,
      }));

      setData({ combined });
      setTotals({
        tokenRevenue: totalTokenRev,
        subRevenue: totalSubRev,
        newUsers: newUsersCount,
        totalUsers: users.length,
      });
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  return (
    <Page title="Revenue & Growth | Fire Investing Admin">
      <Container maxWidth="xl">
        <Box sx={{ pb: 3, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>Revenue & Growth</Typography>
            <Typography variant="body2" color="text.secondary">Time-series charts for token revenue, subscriptions, and user growth</Typography>
          </Box>
          <ToggleButtonGroup size="small" value={range} exclusive onChange={(_, v) => v && setRange(v)}>
            <ToggleButton value="7d">7D</ToggleButton>
            <ToggleButton value="30d">30D</ToggleButton>
            <ToggleButton value="90d">90D</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Summary cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={6} md={3}>
            <StatCard title="Total Token Revenue" value={`$${totals.tokenRevenue?.toFixed(2) || "0.00"}`} sub="All time" color={GREEN} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard title="Total Sub Revenue" value={`$${totals.subRevenue?.toFixed(2) || "0.00"}`} sub="All time" color={ORANGE} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard title="New Users" value={totals.newUsers || 0} sub={`Last ${range === "7d" ? "7" : range === "30d" ? "30" : "90"} days`} color={BLUE} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard title="Total Users" value={totals.totalUsers || 0} sub="Registered" color={PURPLE} />
          </Grid>
        </Grid>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: GREEN }} />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Revenue Area Chart */}
            <Grid item xs={12}>
              <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Revenue Over Time</Typography>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={data.combined || []}>
                    <defs>
                      <linearGradient id="tokenGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={GREEN} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={GREEN} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="subGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={ORANGE} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={ORANGE} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 11 }} />
                    <YAxis tick={{ fill: "#666", fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area type="monotone" dataKey="Token Revenue" stroke={GREEN} fill="url(#tokenGrad)" strokeWidth={2} />
                    <Area type="monotone" dataKey="Sub Revenue" stroke={ORANGE} fill="url(#subGrad)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </Card>
            </Grid>

            {/* User Growth Bar Chart */}
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>New User Sign-ups</Typography>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.combined || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 11 }} />
                    <YAxis tick={{ fill: "#666", fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="New Users" fill={BLUE} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Grid>

            {/* Combined Line */}
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Revenue Comparison</Typography>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={data.combined || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 11 }} />
                    <YAxis tick={{ fill: "#666", fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line type="monotone" dataKey="Token Revenue" stroke={GREEN} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="Sub Revenue" stroke={ORANGE} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </Grid>
          </Grid>
        )}
      </Container>
    </Page>
  );
}
